import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  InputAdornment
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  PhotoCamera as PhotoCameraIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { useLanguage } from '../../contexts/LanguageContext';
import { bookService } from '../../services/bookService';
import { masterDataService } from '../../services/bookService';

function BookDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const isNewBook = id === 'new' || window.location.pathname === '/book/new';

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    isbn: '',
    publisher: '',
    series: '',
    genre: '',
    type: '',
    language: '',
    price: '',
    qty: 1,
    source: '',
    imageUrl: '',
    note: ''
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(isNewBook);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Master data for autocomplete
  const [masterData, setMasterData] = useState({
    publishers: [],
    genres: [],
    types: [],
    languages: [],
    sources: []
  });

  // User's distinct series for autocomplete
  const [userSeries, setUserSeries] = useState([]);

  // Function to get display value in user's language
  const getDisplayValue = (originalValue, type) => {
    if (!originalValue) return '';
    
    const localizedData = getLocalizedMasterData(type);
    const originalData = masterData[type] || [];
    
    // Find the index of the original value in the original data
    const originalIndex = originalData.indexOf(originalValue);
    if (originalIndex !== -1) {
      return localizedData[originalIndex] || originalValue;
    }
    
    return originalValue;
  };

  // Function to get master data in user's language
  const getLocalizedMasterData = (type) => {
    const data = masterData[type] || [];
    
    if (currentLanguage === 'en') {
      // For English, show English names when available, otherwise Thai
      return data.map(item => {
        const englishMatch = item.match(/\(([^)]+)\)/);
        if (englishMatch) {
          return englishMatch[1]; // Return English name
        }
        // For items without English names, provide English translations
        const englishTranslations = {
          // These are now handled by the parentheses format, but keeping for backward compatibility
          'นานมีบุ๊คส์': 'Nanmeebooks',
          'อมรินทร์': 'Amarin',
          'ซีเอ็ด': 'SE-ED',
          'เอ็มไอเอส': 'MIS',
          'ไอแอลแอล': 'ILL',
          'แพรวสำนักพิมพ์': 'Praew Publishing',
          'สำนักพิมพ์มติชน': 'Matichon Publishing',
          'สำนักพิมพ์สยาม': 'Siam Publishing',
          'สำนักพิมพ์แสงดาว': 'Saengdao Publishing',
          'สำนักพิมพ์ผีเสื้อ': 'Pisuea Publishing',
          'สำนักพิมพ์ดอกหญ้า': 'Dokya Publishing',
          'สำนักพิมพ์เรือนแก้ว': 'Ruenkaew Publishing',
          'สำนักพิมพ์ต้นฉบับ': 'Tonchabap Publishing',
          'สำนักพิมพ์ปิยะ': 'Piya Publishing',
          'สำนักพิมพ์ประพันธ์สาส์น': 'Praphan Sarn Publishing',
          'สำนักพิมพ์บรรณกิจ': 'Bannakit Publishing',
          'สำนักพิมพ์บรรณารักษ์': 'Bannaruk Publishing',
          'สำนักพิมพ์บรรณสาร': 'Bannasan Publishing',
          'สำนักพิมพ์บรรณศิลป์': 'Bannasin Publishing',
          'สำนักพิมพ์บรรณสาส์น': 'Bannasarn Publishing',
          'แจ่มใส (Jamsai Publishing)': 'Jamsai Publishing',
          'เอ็นเธอร์บุ๊คส์ (Enter Books)': 'Enter Books',
          'สยามอินเตอร์บุ๊คส์ (Siam Inter Books)': 'Siam Inter Books',
          'อัมรินทร์พริ้นติ้งแอนด์พับลิชชิ่ง (Amarin Printing and Publishing)': 'Amarin Printing and Publishing',
          'สำนักพิมพ์พับลิชชิ่ง (Publishing)': 'Publishing',
          'สยามอินเตอร์คอมิกส์ (Siam Inter Comics)': 'Siam Inter Comics',
          'เนชั่นเอ็ดดูเทนเมนท์ (Nation Edutainment)': 'Nation Edutainment',
          'บงกช พับลิชชิ่ง (Bongkoch Publishing)': 'Bongkoch Publishing',
          'วิบูลย์กิจ (Vibulkij)': 'Vibulkij',
          'รักพิมพ์ พับลิชชิ่ง (Luckpim Publishing)': 'Luckpim Publishing',
          'ฟีนิกซ์ เน็กซ์ (Phoenix Next)': 'Phoenix Next',
          'Meb (Mobile E-books)': 'Meb (Mobile E-books)',
          'จอยลดา (Joylada)': 'Joylada',
          'ธัญวลัย (Tunwalai)': 'Tunwalai',
          'EverY (ในเครือแจ่มใส)': 'EverY',
          'นาบู พับลิชชิ่ง (Nabu Publishing)': 'Nabu Publishing',
          'โรส พับลิชชิ่ง (Rose Publishing)': 'Rose Publishing',
          'เฮอร์มิท บุ๊คส์ (Hermit Books)': 'Hermit Books',
          'พิทบูลเล็ก (Pibunlek)': 'Pibunlek',
          'มีดีส์ (MeeDeeS)': 'MeeDeeS',
          'อมรินทร์ คอมิกส์ (Amarin Comics)': 'Amarin Comics',
          'สปริง บุ๊คส์ (Spring Books)': 'Spring Books',
          'นิยาย': 'Novel',
          'วรรณกรรม': 'Literature',
          'หนังสือเด็ก': 'Children Book',
          'หนังสือเรียน': 'Textbook',
          'หนังสือวิชาการ': 'Academic Book',
          'นิยายรัก': 'Romance Novel',
          'นิยายแฟนตาซี': 'Fantasy Novel',
          'นิยายสยองขวัญ': 'Horror Novel',
          'นิยายสืบสวน': 'Mystery Novel',
          'นิยายประวัติศาสตร์': 'Historical Novel',
          'วรรณกรรมคลาสสิก': 'Classic Literature',
          'วรรณกรรมร่วมสมัย': 'Contemporary Literature',
          'กวีนิพนธ์': 'Poetry',
          'เรื่องสั้น': 'Short Story',
          'สารคดี': 'Non-fiction',
          'หนังสือภาพ': 'Picture Book',
          'หนังสือการ์ตูน': 'Comic Book',
          'หนังสือคู่มือ': 'Manual',
          'หนังสืออ้างอิง': 'Reference Book',
          'หนังสือชีวประวัติ': 'Biography',
          'หนังสือกระดาษ': 'Paper Book',
          'หนังสืออิเล็กทรอนิกส์ (E-book)': 'E-book',
          'ไทย': 'Thai',
          'อังกฤษ': 'English',
          'ร้านหนังสือ': 'Bookstore',
          'ออนไลน์': 'Online',
          'งานหนังสือ': 'Book Fair',
          'มือสอง': 'Second Hand',
          'ห้องสมุด': 'Library',
          'เพื่อน': 'Friend',
          'ครอบครัว': 'Family'
        };
        return englishTranslations[item] || item;
      });
    } else if (currentLanguage === 'ja') {
      // For Japanese, show Japanese translations
      const japaneseTranslations = {
        'นานมีบุ๊คส์': 'ナンミーブックス',
        'อมรินทร์': 'アマリン',
        'ซีเอ็ด': 'SE-ED',
        'เอ็มไอเอส': 'MIS',
        'ไอแอลแอล': 'ILL',
        'นิยาย': '小説',
        'วรรณกรรม': '文学',
        'หนังสือเด็ก': '児童書',
        'หนังสือเรียน': '教科書',
        'หนังสือวิชาการ': '学術書',
        'นิยายรัก': '恋愛小説',
        'นิยายแฟนตาซี': 'ファンタジー小説',
        'นิยายสยองขวัญ': 'ホラー小説',
        'นิยายสืบสวน': '推理小説',
        'นิยายประวัติศาสตร์': '歴史小説',
        'วรรณกรรมคลาสสิク': '古典文学',
        'วรรณกรรมร่วมสมัย': '現代文学',
        'กวีนิพนธ์': '詩',
        'เรื่องสั้น': '短編小説',
        'สารคดี': 'ノンフィクション',
        'หนังสือภาพ': '絵本',
        'หนังสือการ์ตูน': '漫画',
        'หนังสือคู่มือ': 'マニュアル',
        'หนังสืออ้างอิง': '参考書',
        'หนังสือชีวประวัติ': '伝記',
        'หนังสือกระดาษ': '紙の本',
        'หนังสืออิเล็กทรอนิกส์ (E-book)': '電子書籍',
        'ไทย': 'タイ語',
        'อังกฤษ': '英語',
        'ร้านหนังสือ': '書店',
        'ออนไลน์': 'オンライン',
        'งานหนังสือ': 'ブックフェア',
        'มือสอง': '中古',
        'ห้องสมุด': '図書館',
        'เพื่อน': '友達',
        'ครอบครัว': '家族'
      };
      return data.map(item => japaneseTranslations[item] || item);
    } else if (currentLanguage === 'zh') {
      // For Chinese, show Chinese translations
      const chineseTranslations = {
        'นานมีบุ๊คส์': '南美图书',
        'อมรินทร์': '阿玛林',
        'ซีเอ็ด': 'SE-ED',
        'เอ็มไอเอส': 'MIS',
        'ไอแอลแอล': 'ILL',
        'นิยาย': '小说',
        'วรรณกรรม': '文学',
        'หนังสือเด็ก': '儿童书',
        'หนังสือเรียน': '教科书',
        'หนังสือวิชาการ': '学术书',
        'นิยายรัก': '爱情小说',
        'นิยายแฟนตาซี': '奇幻小说',
        'นิยายสยองขวัญ': '恐怖小说',
        'นิยายสืบสวน': '推理小说',
        'นิยายประวัติศาสตร์': '历史小说',
        'วรรณกรรมคลาสสิก': '古典文学',
        'วรรณกรรมร่วมสมัย': '现代文学',
        'กวีนิพนธ์': '诗歌',
        'เรื่องสั้น': '短篇小说',
        'สารคดี': '非虚构',
        'หนังสือภาพ': '图画书',
        'หนังสือการ์ตูน': '漫画',
        'หนังสือคู่มือ': '手册',
        'หนังสืออ้างอิง': '参考书',
        'หนังสือชีวประวัติ': '传记',
        'หนังสือกระดาษ': '纸质书',
        'หนังสืออิเล็กทรอนิกส์ (E-book)': '电子书',
        'ไทย': '泰语',
        'อังกฤษ': '英语',
        'ร้านหนังสือ': '书店',
        'ออนไลน์': '在线',
        'งานหนังสือ': '书展',
        'มือสอง': '二手',
        'ห้องสมุด': '图书馆',
        'เพื่อน': '朋友',
        'ครอบครัว': '家庭'
      };
      return data.map(item => chineseTranslations[item] || item);
    }
    
    // Default to Thai (original data)
    return data;
  };

  // Form validation
  const [errors, setErrors] = useState({});

  const loadBook = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const book = await bookService.getBookById(id);
      if (book) {
        setFormData({
          title: book.title || '',
          author: book.author || '',
          isbn: book.isbn || '',
          publisher: book.publisher || '',
          genre: book.genre || '',
          type: book.type || '',
          language: book.language || '',
          price: book.price || '',
          qty: book.qty || 1,
          source: book.source || '',
          imageUrl: book.imageUrl || '',
          note: book.note || ''
        });
      } else {
        setError('Book not found');
      }
    } catch (error) {
      console.error('Error loading book:', error);
      setError('Failed to load book');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Load master data and user series
  useEffect(() => {
    const loadMasterData = async () => {
      try {
        const data = await masterDataService.getMasterData();
        setMasterData(data);
      } catch (error) {
        console.error('Error loading master data:', error);
      }
    };

    const loadUserSeries = async () => {
      if (!currentUser) return;
      
      try {
        const series = await bookService.getDistinctSeries(currentUser.uid);
        setUserSeries(series);
      } catch (error) {
        console.error('Error loading user series:', error);
      }
    };

    loadMasterData();
    loadUserSeries();
  }, [currentUser]);

  // Load book data if editing existing book
  useEffect(() => {
    if (!isNewBook && id) {
      loadBook();
    }
  }, [id, isNewBook, loadBook]);

  // Function to map localized value back to original Thai value for storage
  const mapLocalizedToOriginal = (localizedValue, type) => {
    if (currentLanguage === 'en') {
      // Map English back to Thai
      const englishToThai = {
        'Nanmeebooks': 'นานมีบุ๊คส์',
        'Amarin': 'อมรินทร์',
        'SE-ED': 'ซีเอ็ด',
        'MIS': 'เอ็มไอเอส',
        'ILL': 'ไอแอลแอล',
        'Jamsai Publishing': 'แจ่มใส (Jamsai Publishing)',
        'Enter Books': 'เอ็นเธอร์บุ๊คส์ (Enter Books)',
        'Siam Inter Books': 'สยามอินเตอร์บุ๊คส์ (Siam Inter Books)',
        'Amarin Printing and Publishing': 'อัมรินทร์พริ้นติ้งแอนด์พับลิชชิ่ง (Amarin Printing and Publishing)',
        'Publishing': 'สำนักพิมพ์พับลิชชิ่ง (Publishing)',
        'Siam Inter Comics': 'สยามอินเตอร์คอมิกส์ (Siam Inter Comics)',
        'Nation Edutainment': 'เนชั่นเอ็ดดูเทนเมนท์ (Nation Edutainment)',
        'Bongkoch Publishing': 'บงกช พับลิชชิ่ง (Bongkoch Publishing)',
        'Vibulkij': 'วิบูลย์กิจ (Vibulkij)',
        'Luckpim Publishing': 'รักพิมพ์ พับลิชชิ่ง (Luckpim Publishing)',
        'Phoenix Next': 'ฟีนิกซ์ เน็กซ์ (Phoenix Next)',
        'Meb (Mobile E-books)': 'Meb (Mobile E-books)',
        'Joylada': 'จอยลดา (Joylada)',
        'Tunwalai': 'ธัญวลัย (Tunwalai)',
        'EverY': 'EverY (ในเครือแจ่มใส)',
        'Nabu Publishing': 'นาบู พับลิชชิ่ง (Nabu Publishing)',
        'Rose Publishing': 'โรส พับลิชชิ่ง (Rose Publishing)',
        'Hermit Books': 'เฮอร์มิท บุ๊คส์ (Hermit Books)',
        'Pibunlek': 'พิทบูลเล็ก (Pibunlek)',
        'MeeDeeS': 'มีดีส์ (MeeDeeS)',
        'Amarin Comics': 'อมรินทร์ คอมิกส์ (Amarin Comics)',
        'Spring Books': 'สปริง บุ๊คส์ (Spring Books)',
        'Novel': 'นิยาย',
        'Literature': 'วรรณกรรม',
        'Children Book': 'หนังสือเด็ก',
        'Textbook': 'หนังสือเรียน',
        'Academic Book': 'หนังสือวิชาการ',
        'Romance Novel': 'นิยายรัก',
        'Fantasy Novel': 'นิยายแฟนตาซี',
        'Horror Novel': 'นิยายสยองขวัญ',
        'Mystery Novel': 'นิยายสืบสวน',
        'Historical Novel': 'นิยายประวัติศาสตร์',
        'Classic Literature': 'วรรณกรรมคลาสสิก',
        'Contemporary Literature': 'วรรณกรรมร่วมสมัย',
        'Poetry': 'กวีนิพนธ์',
        'Short Story': 'เรื่องสั้น',
        'Non-fiction': 'สารคดี',
        'Picture Book': 'หนังสือภาพ',
        'Comic Book': 'หนังสือการ์ตูน',
        'Manual': 'หนังสือคู่มือ',
        'Reference Book': 'หนังสืออ้างอิง',
        'Biography': 'หนังสือชีวประวัติ',
        'Paper Book': 'หนังสือกระดาษ',
        'E-book': 'หนังสืออิเล็กทรอนิกส์ (E-book)',
        'Thai': 'ไทย',
        'English': 'อังกฤษ',
        'Bookstore': 'ร้านหนังสือ',
        'Online': 'ออนไลน์',
        'Book Fair': 'งานหนังสือ',
        'Second Hand': 'มือสอง',
        'Library': 'ห้องสมุด',
        'Friend': 'เพื่อน',
        'Family': 'ครอบครัว'
      };
      return englishToThai[localizedValue] || localizedValue;
    }
    
    // For other languages, return the localized value as is (or implement reverse mapping if needed)
    return localizedValue;
  };

  // Custom filter function for localized autocomplete
  const filterOptions = (options, { inputValue }) => {
    const searchTerm = inputValue.toLowerCase();
    return options.filter(option => {
      const optionLower = option.toLowerCase();
      return optionLower.includes(searchTerm);
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.author.trim()) {
      newErrors.author = 'Author is required';
    }
    
    if (formData.price && isNaN(Number(formData.price))) {
      newErrors.price = 'Price must be a number';
    }
    
    if (formData.qty && (isNaN(Number(formData.qty)) || Number(formData.qty) < 1)) {
      newErrors.qty = 'Quantity must be a positive number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const bookData = {
        ...formData,
        price: formData.price ? Number(formData.price) : 0,
        qty: Number(formData.qty),
        createdDate: isNewBook ? new Date().toISOString() : formData.createdDate
      };

      if (isNewBook) {
        await bookService.createBook(bookData, currentUser.uid);
        setSuccess('Book created successfully!');
      } else {
        await bookService.updateBook(id, bookData);
        setSuccess('Book updated successfully!');
      }

      setIsEditing(false);
      
      // Navigate back to books list after a short delay
      setTimeout(() => {
        navigate('/books');
      }, 1500);
    } catch (error) {
      console.error('Error saving book:', error);
      setError('Failed to save book');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      setError('');
      await bookService.deleteBook(currentUser.uid, id);
      setSuccess('Book deleted successfully!');
      setDeleteDialogOpen(false);
      
      // Navigate back to books list after a short delay
      setTimeout(() => {
        navigate('/books');
      }, 1500);
    } catch (error) {
      console.error('Error deleting book:', error);
      setError('Failed to delete book');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = () => {
    if (isNewBook) {
      navigate('/books');
    } else {
      setIsEditing(false);
      loadBook(); // Reload original data
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/books')}
          variant="outlined"
      >
          {t('common.back')}
      </Button>
      
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          {isNewBook ? t('bookDetail.newBook') : t('bookDetail.editBook')}
      </Typography>
        
        {!isNewBook && !isEditing && (
          <IconButton
            onClick={() => setIsEditing(true)}
            color="primary"
            sx={{ mr: 1 }}
          >
            <EditIcon />
          </IconButton>
        )}
        
        {!isNewBook && (
          <IconButton
            onClick={() => setDeleteDialogOpen(true)}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        )}
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Form */}
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Basic Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('bookDetail.title')}
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                error={!!errors.title}
                helperText={errors.title}
                disabled={!isEditing}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('bookDetail.author')}
                value={formData.author}
                onChange={(e) => handleInputChange('author', e.target.value)}
                error={!!errors.author}
                helperText={errors.author}
                disabled={!isEditing}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('bookDetail.isbn')}
                value={formData.isbn}
                onChange={(e) => handleInputChange('isbn', e.target.value)}
                disabled={!isEditing}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                options={getLocalizedMasterData('publishers')}
                value={getDisplayValue(formData.publisher, 'publishers')}
                onChange={(_, newValue) => handleInputChange('publisher', mapLocalizedToOriginal(newValue || '', 'publishers'))}
                filterOptions={filterOptions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('bookDetail.publisher')}
                    disabled={!isEditing}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                options={userSeries}
                value={formData.series}
                onChange={(_, newValue) => handleInputChange('series', newValue || '')}
                filterOptions={(options, { inputValue }) => {
                  const filtered = options.filter(option =>
                    option.toLowerCase().includes(inputValue.toLowerCase())
                  );
                  // Add the input value if it's not already in the options
                  if (inputValue && !options.includes(inputValue)) {
                    filtered.push(inputValue);
                  }
                  return filtered;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('bookDetail.series')}
                    disabled={!isEditing}
                    placeholder="e.g., Harry Potter, Lord of the Rings"
                  />
                )}
              />
            </Grid>

            {/* Classification */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2, mt: 2 }}>
                Classification
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Autocomplete
                freeSolo
                options={getLocalizedMasterData('genres')}
                value={getDisplayValue(formData.genre, 'genres')}
                onChange={(_, newValue) => handleInputChange('genre', mapLocalizedToOriginal(newValue || '', 'genres'))}
                filterOptions={filterOptions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('bookDetail.genre')}
                    disabled={!isEditing}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Autocomplete
                freeSolo
                options={getLocalizedMasterData('types')}
                value={getDisplayValue(formData.type, 'types')}
                onChange={(_, newValue) => handleInputChange('type', mapLocalizedToOriginal(newValue || '', 'types'))}
                filterOptions={filterOptions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('bookDetail.type')}
                    disabled={!isEditing}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Autocomplete
                freeSolo
                options={getLocalizedMasterData('languages')}
                value={getDisplayValue(formData.language, 'languages')}
                onChange={(_, newValue) => handleInputChange('language', mapLocalizedToOriginal(newValue || '', 'languages'))}
                filterOptions={filterOptions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('bookDetail.language')}
                    disabled={!isEditing}
                  />
                )}
              />
            </Grid>

            {/* Purchase Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2, mt: 2 }}>
                Purchase Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={t('bookDetail.price')}
                type="number"
                value={formData.price}
                onChange={(e) => handleInputChange('price', e.target.value)}
                error={!!errors.price}
                helperText={errors.price}
                disabled={!isEditing}
                InputProps={{
                  startAdornment: <InputAdornment position="start">฿</InputAdornment>,
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={t('bookDetail.quantity')}
                type="number"
                value={formData.qty}
                onChange={(e) => handleInputChange('qty', e.target.value)}
                error={!!errors.qty}
                helperText={errors.qty}
                disabled={!isEditing}
                inputProps={{ min: 1 }}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Autocomplete
                freeSolo
                options={getLocalizedMasterData('sources')}
                value={getDisplayValue(formData.source, 'sources')}
                onChange={(_, newValue) => handleInputChange('source', mapLocalizedToOriginal(newValue || '', 'sources'))}
                filterOptions={filterOptions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('bookDetail.source')}
                    disabled={!isEditing}
                  />
                )}
              />
            </Grid>

            {/* Additional Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2, mt: 2 }}>
                Additional Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('bookDetail.imageUrl')}
                value={formData.imageUrl}
                onChange={(e) => handleInputChange('imageUrl', e.target.value)}
                disabled={!isEditing}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton disabled={!isEditing}>
                        <PhotoCameraIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('bookDetail.notes')}
                value={formData.note}
                onChange={(e) => handleInputChange('note', e.target.value)}
                disabled={!isEditing}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>

          {/* Action Buttons */}
          {isEditing && (
            <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                startIcon={<CancelIcon />}
                disabled={saving}
              >
                {t('common.cancel')}
              </Button>
              
              <Button
                variant="contained"
                onClick={handleSave}
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                disabled={saving}
              >
                {saving ? 'Saving...' : t('bookDetail.save')}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('bookDetail.delete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('bookDetail.deleteConfirm')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('bookDetail.deleteConfirmDescription')}
      </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default BookDetail; 